import React, { useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import type {
  ApproachChildTemplate,
  ApproachReference,
} from 'types/approach';
import TagAtom from '../../atoms/Tag/TagAtom';
import SphereAvatar from '../sphere-avatar/sphere-avatar';
import { useSchemaMetadata } from '../../../hooks/useSchemaMetadata';
import { getLocalizedText } from '../../../utils/getLocalizedText';
import './ChildSpheresMolecule.scss';

export const CHILD_SPHERES_PAGE_SIZE = 12;

export interface ChildSpheresMoleculeProps {
  children: ApproachChildTemplate[];
  /**
   * Renderer for the nested approach detail when an item carries an
   * `approachRef`. Decoupled to avoid a molecule -> organism import.
   */
  renderNestedReader?: (ref: ApproachReference) => React.ReactNode;
  className?: string;
}

interface ChildGroup {
  key: string;
  schemaSystemName: string;
  creationMode: 'auto' | 'recommended';
  items: ApproachChildTemplate[];
}

function groupChildren(items: ApproachChildTemplate[]): ChildGroup[] {
  const map = new Map<string, ChildGroup>();
  for (const child of items) {
    const key = `${child.schemaSystemName}__${child.creationMode}`;
    const existing = map.get(key);
    if (existing) {
      existing.items.push(child);
    } else {
      map.set(key, {
        key,
        schemaSystemName: child.schemaSystemName,
        creationMode: child.creationMode,
        items: [child],
      });
    }
  }
  return Array.from(map.values());
}

interface ChildRowProps {
  child: ApproachChildTemplate;
  renderNestedReader?: (ref: ApproachReference) => React.ReactNode;
}

const ChildRow: React.FC<ChildRowProps> = ({ child, renderNestedReader }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const ref = child.approachRef;
  const canExpand = !!ref && !!renderNestedReader;
  const name = getLocalizedText(child.name) || child.id;
  const description = child.description
    ? getLocalizedText(child.description)
    : '';

  const propsList = child.properties
    ? Object.entries(child.properties)
    : [];

  const conditionText = child.condition?.property;

  return (
    <li className="marketplace-child-spheres__row" data-child-id={child.id}>
      <div className="marketplace-child-spheres__row-header">
        <span className="marketplace-child-spheres__row-name">{name}</span>
        {/* Per-row schema chip removed — the group header already shows the schema. */}
        {canExpand && ref && (
          <button
            type="button"
            className="marketplace-child-spheres__row-toggle"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-label={
              expanded
                ? t(
                    'marketplace.approachDetail.whatsIncluded.childSpheres.collapseRowAria',
                    { name },
                  )
                : t(
                    'marketplace.approachDetail.whatsIncluded.childSpheres.expandRowAria',
                    { name },
                  )
            }
          >
            <i
              className="pi pi-share-alt marketplace-child-spheres__row-toggle-icon"
              aria-hidden="true"
            />
            <span className="marketplace-child-spheres__row-toggle-label">
              {t(
                'marketplace.approachDetail.whatsIncluded.childSpheres.nestedApproachLabel',
                { name: ref.approachSystemName },
              )}
            </span>
            <i
              className={`pi ${
                expanded ? 'pi-chevron-up' : 'pi-chevron-down'
              } marketplace-child-spheres__row-toggle-chevron`}
              aria-hidden="true"
            />
          </button>
        )}
      </div>
      {description && (
        <p className="marketplace-child-spheres__row-description">{description}</p>
      )}
      {conditionText && (
        <div className="marketplace-child-spheres__row-condition">
          <i className="pi pi-filter" aria-hidden="true" />
          <span>
            {t(
              'marketplace.approachDetail.whatsIncluded.childSpheres.conditionLabel',
              { condition: conditionText },
            )}
          </span>
        </div>
      )}
      {propsList.length > 0 && (
        <div className="marketplace-child-spheres__row-props">
          {propsList.map(([k, v]) => (
            <span key={k} className="marketplace-child-spheres__row-prop">
              <i
                className="pi pi-cog marketplace-child-spheres__row-prop-icon"
                aria-hidden="true"
              />
              <span className="marketplace-child-spheres__row-prop-key">{k}:</span>
              <b className="marketplace-child-spheres__row-prop-value">{String(v)}</b>
            </span>
          ))}
        </div>
      )}
      {canExpand && expanded && ref && renderNestedReader && (
        <div className="marketplace-child-spheres__row-nested">
          {renderNestedReader(ref)}
        </div>
      )}
    </li>
  );
};

interface ChildGroupRowProps {
  group: ChildGroup;
  renderNestedReader?: (ref: ApproachReference) => React.ReactNode;
}

const ChildGroupRow: React.FC<ChildGroupRowProps> = ({
  group,
  renderNestedReader,
}) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState('');

  const schemaSystemNames = useMemo(
    () => [group.schemaSystemName],
    [group.schemaSystemName],
  );
  const { resolved: schemaMetadata } = useSchemaMetadata(schemaSystemNames);
  const schemaInfo = schemaMetadata.get(group.schemaSystemName);
  const schemaLabel =
    schemaInfo?.displayName ?? group.schemaSystemName;
  const schemaGlyph = schemaInfo?.glyph ?? undefined;
  const schemaGlyphColor = schemaInfo?.glyphColor;

  const isLarge = group.items.length > CHILD_SPHERES_PAGE_SIZE;
  const trimmedSearch = search.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!trimmedSearch) return group.items;
    return group.items.filter((c) => {
      const name = (getLocalizedText(c.name) || c.id).toLowerCase();
      return name.includes(trimmedSearch);
    });
  }, [group.items, trimmedSearch]);

  const visible =
    isLarge && !showAll && !trimmedSearch
      ? filtered.slice(0, CHILD_SPHERES_PAGE_SIZE)
      : filtered;
  const hidden = filtered.length - visible.length;

  const modeLabel =
    group.creationMode === 'auto'
      ? t(
          'marketplace.approachDetail.whatsIncluded.childSpheres.creationMode.auto',
        )
      : t(
          'marketplace.approachDetail.whatsIncluded.childSpheres.creationMode.recommended',
        );

  const countLabel = t(
    group.items.length === 1
      ? 'marketplace.approachDetail.whatsIncluded.childSpheres.groupCount'
      : 'marketplace.approachDetail.whatsIncluded.childSpheres.groupCount_plural',
    { count: group.items.length },
  );

  return (
    <div className="marketplace-child-spheres__group" data-group-key={group.key}>
      <div className="marketplace-child-spheres__group-header">
        <span className="marketplace-child-spheres__group-schema">
          <SphereAvatar
            glyph={schemaGlyph}
            glyphColor={schemaGlyphColor}
            size="xsmall"
            shape="circle"
            altText={schemaLabel}
          />
          <span className="marketplace-child-spheres__group-name">{schemaLabel}</span>
        </span>
        <span className="marketplace-child-spheres__group-count">{countLabel}</span>
        <TagAtom
          tone={group.creationMode === 'auto' ? 'success' : 'outline'}
          label={modeLabel}
        />
        {isLarge && (
          <input
            type="text"
            className="marketplace-child-spheres__group-filter"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t(
              'marketplace.approachDetail.whatsIncluded.childSpheres.filterPlaceholder',
            )}
            aria-label={t(
              'marketplace.approachDetail.whatsIncluded.childSpheres.filterPlaceholder',
            )}
          />
        )}
      </div>
      <ul className="marketplace-child-spheres__group-list">
        {visible.map((child) => (
          <ChildRow
            key={child.id}
            child={child}
            renderNestedReader={renderNestedReader}
          />
        ))}
      </ul>
      {hidden > 0 && (
        <button
          type="button"
          className="marketplace-child-spheres__show-more"
          onClick={() => setShowAll(true)}
        >
          <i className="pi pi-chevron-down" aria-hidden="true" />
          {t(
            'marketplace.approachDetail.whatsIncluded.childSpheres.showMore',
            { count: hidden },
          )}
        </button>
      )}
      {showAll && filtered.length > CHILD_SPHERES_PAGE_SIZE && !trimmedSearch && (
        <button
          type="button"
          className="marketplace-child-spheres__collapse"
          onClick={() => setShowAll(false)}
        >
          <i className="pi pi-chevron-up" aria-hidden="true" />
          {t(
            'marketplace.approachDetail.whatsIncluded.childSpheres.collapseGroup',
          )}
        </button>
      )}
      {trimmedSearch && filtered.length === 0 && (
        <p className="marketplace-child-spheres__empty">
          {t(
            'marketplace.approachDetail.whatsIncluded.childSpheres.noMatches',
          )}
        </p>
      )}
    </div>
  );
};

const ChildSpheresMolecule: React.FC<ChildSpheresMoleculeProps> = ({
  children,
  renderNestedReader,
  className = '',
}) => {
  const groups = useMemo(() => groupChildren(children), [children]);

  if (groups.length === 0) return null;

  const rootClasses = ['marketplace-child-spheres', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClasses}>
      {groups.map((group) => (
        <ChildGroupRow
          key={group.key}
          group={group}
          renderNestedReader={renderNestedReader}
        />
      ))}
    </div>
  );
};

ChildSpheresMolecule.displayName = 'ChildSpheresMolecule';

export default ChildSpheresMolecule;
